import { useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services'
import type { Image } from '@/types'

/**
 * Query key factory for images.
 */
export const imageQueryKeys = {
  byReport: (reportId: string) => ['images', { reportId }] as const,
}

/**
 * Response from creating an upload URL.
 */
export interface CreateUploadURLResponse {
  image_id: string
  upload_url: string
  expires_at: string
}

/** How long to keep polling for pending images before giving up. */
export const PENDING_IMAGE_POLL_TIMEOUT_MS = 60_000

/** How often to poll while any image is pending. */
export const PENDING_IMAGE_POLL_INTERVAL_MS = 2_000

/**
 * Hook to fetch images for a report.
 *
 * While any image returned has `status === 'pending'`, the query refetches
 * every ~2s so the UI can observe when the S3 event handler flips status
 * to `confirmed`. Polling stops once no pending images remain, and also stops
 * after PENDING_IMAGE_POLL_TIMEOUT_MS of continuous pending state so a dropped
 * S3 event does not cause indefinite polling.
 */
export function useReportImages(reportId: string) {
  // Tracks when the current run of pending-state polling began. Reset to null
  // whenever data settles (no pending), so the timeout restarts for future runs.
  const pollStartRef = useRef<number | null>(null)

  return useQuery({
    queryKey: imageQueryKeys.byReport(reportId),
    queryFn: () => api.get<Image[]>(`/reports/${reportId}/images`),
    enabled: !!reportId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data || !data.some((img) => img.status === 'pending')) {
        pollStartRef.current = null
        return false
      }
      if (pollStartRef.current === null) {
        pollStartRef.current = Date.now()
      }
      if (Date.now() - pollStartRef.current > PENDING_IMAGE_POLL_TIMEOUT_MS) {
        return false
      }
      return PENDING_IMAGE_POLL_INTERVAL_MS
    },
  })
}

/** Maximum allowed size for an uploaded image, in bytes. Must match the server-side cap. */
export const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024

/**
 * Hook to create an upload URL for an image.
 */
export function useCreateImageUploadURL(reportId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { content_type: string; filename?: string; content_length: number }) =>
      api.post<CreateUploadURLResponse>(`/reports/${reportId}/images`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: imageQueryKeys.byReport(reportId) })
    },
  })
}

/**
 * Hook to delete an image.
 */
export function useDeleteImage(reportId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (imageId: string) =>
      api.delete(`/reports/${reportId}/images/${imageId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: imageQueryKeys.byReport(reportId) })
    },
  })
}

/**
 * Upload a file directly to S3 using a presigned URL.
 * Returns the XMLHttpRequest for progress tracking.
 */
export function uploadToS3(
  uploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100)
        onProgress(progress)
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to a network error'))
    })

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'))
    })

    xhr.open('PUT', uploadUrl)
    xhr.setRequestHeader('Content-Type', file.type)
    xhr.send(file)
  })
}
